import _slicedToArray from "/home/runner/.github-runner/_work/dfh-corp-sites/dfh-corp-sites/node_modules/.pnpm/@babel+runtime@7.27.1/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _defineProperty from "/home/runner/.github-runner/_work/dfh-corp-sites/dfh-corp-sites/node_modules/.pnpm/@babel+runtime@7.27.1/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { getAsyncContextStrategy } from '../asyncContext/index.js';
import { getMainCarrier } from '../carrier.js';
import { withScope, getCurrentScope, getClient, getIsolationScope } from '../currentScopes.js';
import { DEBUG_BUILD } from '../debug-build.js';
import { SEMANTIC_ATTRIBUTE_SENTRY_SAMPLE_RATE, SEMANTIC_ATTRIBUTE_SENTRY_SOURCE } from '../semanticAttributes.js';
import { baggageHeaderToDynamicSamplingContext } from '../utils/baggage.js';
import { debug } from '../utils/debug-logger.js';
import { handleCallbackErrors } from '../utils/handleCallbackErrors.js';
import { hasSpansEnabled } from '../utils/hasSpansEnabled.js';
import { parseSampleRate } from '../utils/parseSampleRate.js';
import { generateTraceId } from '../utils/propagationContext.js';
import { _setSpanForScope, _getSpanForScope } from '../utils/spanOnScope.js';
import { spanToJSON, spanTimeInputToSeconds, getRootSpan, addChildSpanToSpan, spanIsSampled } from '../utils/spanUtils.js';
import { shouldContinueTrace, propagationContextFromHeaders } from '../utils/tracing.js';
import { getDynamicSamplingContextFromSpan, freezeDscOnSpan } from './dynamicSamplingContext.js';
import { logSpanStart } from './logSpans.js';
import { sampleSpan } from './sampling.js';
import { SentryNonRecordingSpan } from './sentryNonRecordingSpan.js';
import { SentrySpan } from './sentrySpan.js';
import { SPAN_STATUS_ERROR } from './spanstatus.js';
import { setCapturedScopesOnSpan } from './utils.js';

/* eslint-disable max-lines */

var SUPPRESS_TRACING_KEY = '__SENTRY_SUPPRESS_TRACING__';

/**
 * Wraps a function with a transaction/span and finishes the span after the function is done.
 * The created span is the active span and will be used as parent by other spans created inside the function
 * and can be accessed via `Sentry.getActiveSpan()`, as long as the function is executed while the scope is active.
 *
 * If you want to create a span that is not set as active, use {@link startInactiveSpan}.
 *
 * You'll always get a span passed to the callback,
 * it may just be a non-recording span if the span is not sampled or if tracing is disabled.
 */
function startSpan(options, callback) {
  var acs = getAcs();
  if (acs.startSpan) {
    return acs.startSpan(options, callback);
  }
  var spanArguments = parseSentrySpanArguments(options);
  var forceTransaction = options.forceTransaction,
    customParentSpan = options.parentSpan,
    customScope = options.scope;

  // We still need to fork a potentially passed scope, as we set the active span on it
  // and we need to ensure that it is cleaned up properly once the span ends.
  var customForkedScope = customScope === null || customScope === void 0 ? void 0 : customScope.clone();
  return withScope(customForkedScope, function () {
    // If `options.parentSpan` is defined, we want to wrap the callback in `withActiveSpan`
    var wrapper = getActiveSpanWrapper(customParentSpan);
    return wrapper(function () {
      var scope = getCurrentScope();
      var parentSpan = getParentSpan(scope, customParentSpan);
      var shouldSkipSpan = options.onlyIfParent && !parentSpan;
      var activeSpan = shouldSkipSpan ? new SentryNonRecordingSpan() : createChildOrRootSpan({
        parentSpan: parentSpan,
        spanArguments: spanArguments,
        forceTransaction: forceTransaction,
        scope: scope
      });
      _setSpanForScope(scope, activeSpan);
      return handleCallbackErrors(function () {
        return callback(activeSpan);
      }, function () {
        // Only update the span status if it hasn't been changed yet, and the span is not yet finished
        var _spanToJSON = spanToJSON(activeSpan),
          status = _spanToJSON.status;
        if (activeSpan.isRecording() && (!status || status === 'ok')) {
          activeSpan.setStatus({
            code: SPAN_STATUS_ERROR,
            message: 'internal_error'
          });
        }
      }, function () {
        activeSpan.end();
      });
    });
  });
}

/**
 * Similar to `Sentry.startSpan`. Wraps a function with a transaction/span, but does not finish the span
 * after the function is done automatically. Use `span.end()` to end the span.
 *
 * The created span is the active span and will be used as parent by other spans created inside the function
 * and can be accessed via `Sentry.getActiveSpan()`, as long as the function is executed while the scope is active.
 *
 * You'll always get a span passed to the callback,
 * it may just be a non-recording span if the span is not sampled or if tracing is disabled.
 */
function startSpanManual(options, callback) {
  var acs = getAcs();
  if (acs.startSpanManual) {
    return acs.startSpanManual(options, callback);
  }
  var spanArguments = parseSentrySpanArguments(options);
  var forceTransaction = options.forceTransaction,
    customParentSpan = options.parentSpan,
    customScope = options.scope;
  var customForkedScope = customScope === null || customScope === void 0 ? void 0 : customScope.clone();
  return withScope(customForkedScope, function () {
    // If `options.parentSpan` is defined, we want to wrap the callback in `withActiveSpan`
    var wrapper = getActiveSpanWrapper(customParentSpan);
    return wrapper(function () {
      var scope = getCurrentScope();
      var parentSpan = getParentSpan(scope, customParentSpan);
      var shouldSkipSpan = options.onlyIfParent && !parentSpan;
      var activeSpan = shouldSkipSpan ? new SentryNonRecordingSpan() : createChildOrRootSpan({
        parentSpan: parentSpan,
        spanArguments: spanArguments,
        forceTransaction: forceTransaction,
        scope: scope
      });
      _setSpanForScope(scope, activeSpan);
      return handleCallbackErrors(
      // We pass the `finish` function to the callback, so the user can finish the span manually
      // this is mainly here for historic purposes because previously, we instructed users to call
      // `finish` instead of `span.end()` to also clean up the scope. Nowadays, calling `span.end()`
      // or `finish` has the same effect and we simply leave it here to avoid breaking user code.
      function () {
        return callback(activeSpan, function () {
          return activeSpan.end();
        });
      }, function () {
        // Only update the span status if it hasn't been changed yet, and the span is not yet finished
        var _spanToJSON2 = spanToJSON(activeSpan),
          status = _spanToJSON2.status;
        if (activeSpan.isRecording() && (!status || status === 'ok')) {
          activeSpan.setStatus({
            code: SPAN_STATUS_ERROR,
            message: 'internal_error'
          });
        }
      });
    });
  });
}

/**
 * Creates a span. This span is not set as active, so will not get automatic instrumentation spans
 * as children or be able to be accessed via `Sentry.getActiveSpan()`.
 *
 * If you want to create a span that is set as active, use {@link startSpan}.
 *
 * This function will always return a span,
 * it may just be a non-recording span if the span is not sampled or if tracing is disabled.
 */
function startInactiveSpan(options) {
  var acs = getAcs();
  if (acs.startInactiveSpan) {
    return acs.startInactiveSpan(options);
  }
  var spanArguments = parseSentrySpanArguments(options);
  var forceTransaction = options.forceTransaction,
    customParentSpan = options.parentSpan;

  // If `options.scope` is defined, we use this as as a wrapper,
  // If `options.parentSpan` is defined, we want to wrap the callback in `withActiveSpan`
  var wrapper = options.scope ? function (callback) {
    return withScope(options.scope, callback);
  } : customParentSpan !== undefined ? function (callback) {
    return withActiveSpan(customParentSpan, callback);
  } : function (callback) {
    return callback();
  };
  return wrapper(function () {
    var scope = getCurrentScope();
    var parentSpan = getParentSpan(scope, customParentSpan);
    var shouldSkipSpan = options.onlyIfParent && !parentSpan;
    if (shouldSkipSpan) {
      return new SentryNonRecordingSpan();
    }
    return createChildOrRootSpan({
      parentSpan: parentSpan,
      spanArguments: spanArguments,
      forceTransaction: forceTransaction,
      scope: scope
    });
  });
}

/**
 * Continue a trace from `sentry-trace` and `baggage` values.
 * These values can be obtained from incoming request headers, or in the browser from `<meta name="sentry-trace">`
 * and `<meta name="baggage">` HTML tags.
 *
 * Spans started with `startSpan`, `startSpanManual` and `startInactiveSpan`, within the callback will automatically
 * be attached to the incoming trace.
 */
var continueTrace = function continueTrace(options, callback) {
  var carrier = getMainCarrier();
  var acs = getAsyncContextStrategy(carrier);
  if (acs.continueTrace) {
    return acs.continueTrace(options, callback);
  }
  var sentryTrace = options.sentryTrace,
    baggage = options.baggage;
  var client = getClient();
  var incomingDsc = baggageHeaderToDynamicSamplingContext(baggage);
  if (client && !shouldContinueTrace(client, incomingDsc === null || incomingDsc === void 0 ? void 0 : incomingDsc.org_id)) {
    return startNewTrace(callback);
  }
  return withScope(function (scope) {
    var propagationContext = propagationContextFromHeaders(sentryTrace, baggage);
    scope.setPropagationContext(propagationContext);
    return callback();
  });
};

/**
 * Forks the current scope and sets the provided span as active span in the context of the provided callback. Can be
 * passed `null` to start an entirely new span tree.
 *
 * @param span Spans started in the context of the provided callback will be children of this span. If `null` is passed,
 * spans started within the callback will not be attached to a parent span.
 * @param callback Execution context in which the provided span will be active. Is passed the newly forked scope.
 * @returns the value returned from the provided callback function.
 */
function withActiveSpan(span, callback) {
  var acs = getAcs();
  if (acs.withActiveSpan) {
    return acs.withActiveSpan(span, callback);
  }
  return withScope(function (scope) {
    _setSpanForScope(scope, span || undefined);
    return callback(scope);
  });
}

/** Suppress tracing in the given callback, ensuring no spans are generated inside of it. */
function suppressTracing(callback) {
  var acs = getAcs();
  if (acs.suppressTracing) {
    return acs.suppressTracing(callback);
  }
  return withScope(function (scope) {
    // Note: We do not wait for the callback to finish before we reset the metadata
    // the reason for this is that otherwise, in the browser this can lead to very weird behavior
    // as there is only a single top scope, if the callback takes longer to finish,
    // other, unrelated spans may also be suppressed, which we do not want
    // so instead, we only suppress tracing synchronoysly in the browser
    scope.setSDKProcessingMetadata(_defineProperty({}, SUPPRESS_TRACING_KEY, true));
    var res = callback();
    scope.setSDKProcessingMetadata(_defineProperty({}, SUPPRESS_TRACING_KEY, undefined));
    return res;
  });
}

/**
 * Starts a new trace for the duration of the provided callback. Spans started within the
 * callback will be part of the new trace instead of a potentially previously started trace.
 *
 * Important: Only use this function if you want to override the default trace lifetime and
 * propagation mechanism of the SDK for the duration and scope of the provided callback.
 * The newly created trace will also be the root of a new distributed trace, for example if
 * you make http requests within the callback.
 * This function might be useful if the operation you want to instrument should not be part
 * of a potentially ongoing trace.
 *
 * Default behavior:
 * - Server-side: A new trace is started for each incoming request.
 * - Browser: A new trace is started for each page our route. Navigating to a new route
 *            or page will automatically create a new trace.
 */
function startNewTrace(callback) {
  return withScope(function (scope) {
    scope.setPropagationContext({
      traceId: generateTraceId(),
      sampleRand: Math.random()
    });
    DEBUG_BUILD && debug.log("Starting a new trace with id ".concat(scope.getPropagationContext().traceId));
    return withActiveSpan(null, callback);
  });
}
function createChildOrRootSpan(_ref) {
  var parentSpan = _ref.parentSpan,
    spanArguments = _ref.spanArguments,
    forceTransaction = _ref.forceTransaction,
    scope = _ref.scope;
  if (!hasSpansEnabled()) {
    var _span = new SentryNonRecordingSpan();

    // If this is a root span, we ensure to freeze a DSC
    // So we can have at least partial data here
    if (forceTransaction || !parentSpan) {
      var dsc = _objectSpread({
        sampled: 'false',
        sample_rate: '0',
        transaction: spanArguments.name
      }, getDynamicSamplingContextFromSpan(_span));
      freezeDscOnSpan(_span, dsc);
    }
    return _span;
  }
  var isolationScope = getIsolationScope();
  var span;
  if (parentSpan && !forceTransaction) {
    span = _startChildSpan(parentSpan, scope, spanArguments);
    addChildSpanToSpan(parentSpan, span);
  } else if (parentSpan) {
    // If we forced a transaction but have a parent span, make sure to continue from the parent span, not the scope
    var _dsc = getDynamicSamplingContextFromSpan(parentSpan);
    var _parentSpan$spanConte = parentSpan.spanContext(),
      traceId = _parentSpan$spanConte.traceId,
      parentSpanId = _parentSpan$spanConte.spanId;
    var parentSampled = spanIsSampled(parentSpan);
    span = _startRootSpan(_objectSpread({
      traceId: traceId,
      parentSpanId: parentSpanId
    }, spanArguments), scope, parentSampled);
    freezeDscOnSpan(span, _dsc);
  } else {
    var _isolationScope$getPr = _objectSpread(_objectSpread({}, isolationScope.getPropagationContext()), scope.getPropagationContext()),
      _traceId = _isolationScope$getPr.traceId,
      _dsc2 = _isolationScope$getPr.dsc,
      _parentSpanId = _isolationScope$getPr.parentSpanId,
      _parentSampled = _isolationScope$getPr.sampled;
    span = _startRootSpan(_objectSpread({
      traceId: _traceId,
      parentSpanId: _parentSpanId
    }, spanArguments), scope, _parentSampled);
    if (_dsc2) {
      freezeDscOnSpan(span, _dsc2);
    }
  }
  logSpanStart(span);
  setCapturedScopesOnSpan(span, scope, isolationScope);
  return span;
}

/**
 * This converts StartSpanOptions to SentrySpanArguments.
 * For the most part (for now) we accept the same options,
 * but some of them need to be transformed.
 */
function parseSentrySpanArguments(options) {
  var exp = options.experimental || {};
  var initialCtx = _objectSpread({
    isStandalone: exp.standalone
  }, options);
  if (options.startTime) {
    var ctx = _objectSpread({}, initialCtx);
    ctx.startTimestamp = spanTimeInputToSeconds(options.startTime);
    delete ctx.startTime;
    return ctx;
  }
  return initialCtx;
}
function getAcs() {
  var carrier = getMainCarrier();
  return getAsyncContextStrategy(carrier);
}
function _startRootSpan(spanArguments, scope, parentSampled) {
  var _mutableSpanSamplingD, _currentPropagationCo;
  var client = getClient();
  var options = (client === null || client === void 0 ? void 0 : client.getOptions()) || {};
  var _spanArguments$name = spanArguments.name,
    name = _spanArguments$name === void 0 ? '' : _spanArguments$name;
  var mutableSpanSamplingData = {
    spanAttributes: _objectSpread({}, spanArguments.attributes),
    spanName: name,
    parentSampled: parentSampled
  };

  // we don't care about the decision for the moment; this is just a placeholder
  client === null || client === void 0 || client.emit('beforeSampling', mutableSpanSamplingData, {
    decision: false
  });

  // If hook consumers override the parentSampled flag, we will use that value instead of the actual one
  var finalParentSampled = (_mutableSpanSamplingD = mutableSpanSamplingData.parentSampled) !== null && _mutableSpanSamplingD !== void 0 ? _mutableSpanSamplingD : parentSampled;
  var finalAttributes = mutableSpanSamplingData.spanAttributes;
  var currentPropagationContext = scope.getPropagationContext();
  var _ref2 = scope.getScopeData().sdkProcessingMetadata[SUPPRESS_TRACING_KEY] ? [false] : sampleSpan(options, {
      name: name,
      parentSampled: finalParentSampled,
      attributes: finalAttributes,
      parentSampleRate: parseSampleRate((_currentPropagationCo = currentPropagationContext.dsc) === null || _currentPropagationCo === void 0 ? void 0 : _currentPropagationCo.sample_rate)
    }, currentPropagationContext.sampleRand),
    _ref3 = _slicedToArray(_ref2, 3),
    sampled = _ref3[0],
    sampleRate = _ref3[1],
    localSampleRateWasApplied = _ref3[2];
  var rootSpan = new SentrySpan(_objectSpread(_objectSpread({}, spanArguments), {}, {
    attributes: _objectSpread(_defineProperty(_defineProperty({}, SEMANTIC_ATTRIBUTE_SENTRY_SOURCE, 'custom'), SEMANTIC_ATTRIBUTE_SENTRY_SAMPLE_RATE, sampleRate !== undefined && localSampleRateWasApplied ? sampleRate : undefined), finalAttributes),
    sampled: sampled
  }));
  if (!sampled && client) {
    DEBUG_BUILD && debug.log('[Tracing] Discarding root span because its trace was not chosen to be sampled.');
    client.recordDroppedEvent('sample_rate', 'transaction');
  }
  if (client) {
    client.emit('spanStart', rootSpan);
  }
  return rootSpan;
}

/**
 * Creates a new `Span` while setting the current `Span.id` as `parentSpanId`.
 * This inherits the sampling decision from the parent span.
 */
function _startChildSpan(parentSpan, scope, spanArguments) {
  var _parentSpan$spanConte2 = parentSpan.spanContext(),
    spanId = _parentSpan$spanConte2.spanId,
    traceId = _parentSpan$spanConte2.traceId;
  var sampled = scope.getScopeData().sdkProcessingMetadata[SUPPRESS_TRACING_KEY] ? false : spanIsSampled(parentSpan);
  var childSpan = sampled ? new SentrySpan(_objectSpread(_objectSpread({}, spanArguments), {}, {
    parentSpanId: spanId,
    traceId: traceId,
    sampled: sampled
  })) : new SentryNonRecordingSpan({
    traceId: traceId
  });
  addChildSpanToSpan(parentSpan, childSpan);
  var client = getClient();
  if (client) {
    client.emit('spanStart', childSpan);
    // If it has an endTimestamp, it's already ended
    if (spanArguments.endTimestamp) {
      client.emit('spanEnd', childSpan);
    }
  }
  return childSpan;
}
function getParentSpan(scope, customParentSpan) {
  // always use the passed in span directly
  if (customParentSpan) {
    return customParentSpan;
  }

  // This is different from `undefined` as it means the user explicitly wants no parent span
  if (customParentSpan === null) {
    return undefined;
  }
  var span = _getSpanForScope(scope);
  if (!span) {
    return undefined;
  }
  var client = getClient();
  var options = client ? client.getOptions() : {};
  if (options.parentSpanIsAlwaysRootSpan) {
    return getRootSpan(span);
  }
  return span;
}
function getActiveSpanWrapper(parentSpan) {
  return parentSpan !== undefined ? function (callback) {
    return withActiveSpan(parentSpan, callback);
  } : function (callback) {
    return callback();
  };
}
export { continueTrace, startInactiveSpan, startNewTrace, startSpan, startSpanManual, suppressTracing, withActiveSpan };